import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

/**
 * Combobox: Filter-Dropdown mit eingebauter Volltextsuche.
 *
 * - <input role="combobox"> + <ul role="listbox"> nach WAI-ARIA APG
 * - Pfeiltasten + Enter funktionieren über Roving-aria-activedescendant
 * - Single-Select-Variante (Multi-Select wäre ein eigener Component)
 * - Wird für alle Filter (Kunde, Standort, Service-Typ, ...) verwendet
 * - Optional serverseitige Suche: `onQueryChange` reicht die Eingabe an den
 *   Parent (der lädt Optionen nach), `filterOptions={false}` schaltet den
 *   eingebauten Substring-Filter ab, `footerHint` zeigt z.B. einen
 *   "Ergebnis gekappt"-Hinweis unter der Liste.
 *
 * Dependency-frei (kein @headlessui, kein Downshift).
 */

export interface ComboboxOption {
  value: string;
  label: string;
  /** Optionaler Sub-Hinweis (z.B. "23 Systeme"). */
  hint?: string | undefined;
  disabled?: boolean;
}

interface ComboboxProps {
  /** Sichtbares Label. Kann leer sein, dann muss `ariaLabel` gesetzt werden. */
  label: string;
  /** Versteckt das Label visuell (sr-only), nützlich in dichten Tabellen. */
  hideLabel?: boolean;
  /** Wenn label leer ist, bitte ariaLabel setzen. */
  ariaLabel?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: ComboboxOption[];
  placeholder?: string;
  /** Wenn gesetzt, kein leerer Default-Eintrag - Pflichtwahl. */
  required?: boolean;
  /** Externer Disabled-Zustand. */
  disabled?: boolean;
  /** Erscheint als Hint-Text unter dem Label. */
  hint?: string;
  className?: string;
  /**
   * Reicht die Sucheingabe an den Parent (serverseitige Suche). Wird auch
   * beim Zurücksetzen (Commit) mit '' aufgerufen.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Eingebauten Substring-Filter abschalten (false), wenn der Parent die
   * Optionen bereits passend zur Query liefert (serverseitige Suche).
   * Default true (bisheriges Verhalten).
   */
  filterOptions?: boolean;
  /** Nicht-interaktiver Hinweis unter der Optionsliste (z.B. "gekappt"). */
  footerHint?: string | undefined;
  /** Text bei leerer Optionsliste (Default "Keine Treffer."). */
  emptyText?: string;
}

export function Combobox({
  label,
  hideLabel = false,
  ariaLabel,
  value,
  onChange,
  options,
  placeholder = 'Auswählen ...',
  required = false,
  disabled = false,
  hint,
  className,
  onQueryChange,
  filterOptions = true,
  footerHint,
  emptyText = 'Keine Treffer.',
}: ComboboxProps): JSX.Element {
  const idPrefix = useId();
  const inputId = `${idPrefix}-input`;
  const listboxId = `${idPrefix}-list`;
  const hintId = hint ? `${idPrefix}-hint` : undefined;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!filterOptions || !query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, query, filterOptions]);

  const selected = options.find((o) => o.value === value) ?? null;

  // Outside-Click schließt.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const commit = (option: ComboboxOption | null): void => {
    if (option?.disabled) return;
    onChange(option ? option.value : null);
    setOpen(false);
    setQuery('');
    onQueryChange?.('');
  };

  const onKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setOpen(true);
      event.preventDefault();
      return;
    }
    if (!open) return;
    if (event.key === 'ArrowDown') {
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      setActiveIdx((i) => Math.max(i - 1, 0));
      event.preventDefault();
    } else if (event.key === 'Enter') {
      const opt = filtered[activeIdx];
      if (opt) commit(opt);
      event.preventDefault();
    } else if (event.key === 'Escape') {
      setOpen(false);
      event.preventDefault();
    }
  };

  return (
    <div ref={rootRef} className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      {label && (
        <label
          htmlFor={inputId}
          className={[
            'text-sm font-medium text-text',
            hideLabel ? 'sr-only' : '',
          ].join(' ')}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-muted">
          <Search className="size-4" aria-hidden="true" />
        </span>
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-label={!label && ariaLabel ? ariaLabel : undefined}
          aria-activedescendant={
            open && filtered[activeIdx] ? `${idPrefix}-opt-${filtered[activeIdx].value}` : undefined
          }
          aria-describedby={hintId}
          disabled={disabled}
          placeholder={selected ? selected.label : placeholder}
          value={open ? query : (selected?.label ?? '')}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            onQueryChange?.(event.target.value);
            setActiveIdx(0);
            if (!open) setOpen(true);
          }}
          onKeyDown={onKey}
          className="w-full rounded-md border border-border bg-surface px-8 py-2 text-sm text-text placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span className="absolute inset-y-0 right-1 flex items-center gap-0.5">
          {!required && selected && (
            <button
              type="button"
              aria-label="Auswahl löschen"
              onClick={() => commit(null)}
              className="rounded p-1 text-muted hover:bg-surface-2"
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          )}
          <span className="px-1 text-muted" aria-hidden="true">
            <ChevronDown className="size-4" />
          </span>
        </span>

        {open && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-surface py-1 text-sm shadow-lg"
          >
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-muted" role="presentation">
                {emptyText}
              </li>
            )}
            {filtered.map((opt, idx) => {
              const isActive = idx === activeIdx;
              const isSelected = opt.value === value;
              return (
                <li
                  key={opt.value}
                  id={`${idPrefix}-opt-${opt.value}`}
                  role="option"
                  aria-selected={isSelected}
                  data-disabled={opt.disabled ? '' : undefined}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseDown={(event) => {
                    // mousedown statt click - sonst verliert das Input vorher Focus.
                    event.preventDefault();
                    commit(opt);
                  }}
                  className={[
                    'flex cursor-pointer items-center justify-between px-3 py-1.5',
                    isActive ? 'bg-surface-2' : '',
                    opt.disabled ? 'cursor-not-allowed opacity-50' : '',
                  ].join(' ')}
                >
                  <span>
                    <span className={isSelected ? 'font-medium' : ''}>{opt.label}</span>
                    {opt.hint && <span className="ml-2 text-xs text-muted">{opt.hint}</span>}
                  </span>
                  {isSelected && <Check className="size-4 text-primary" aria-hidden="true" />}
                </li>
              );
            })}
            {footerHint && (
              <li
                className="border-t border-border px-3 py-1.5 text-xs text-muted"
                role="presentation"
              >
                {footerHint}
              </li>
            )}
          </ul>
        )}
      </div>
      {hint && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
